// features/projects/templates/continuum/output.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM OUTPUT — parent tab for audience-facing artifacts
//
// Groups the two tabs that produce content for stakeholders:
// Publications (Claude-generated markdown documents — paper
// outline, policy brief, talk outline) and Donor Brief (curated
// data-driven view).
//
// They differ in production method (Claude generation vs.
// structured aggregation) but share a purpose: synthesizing
// research into something a non-researcher would actually read.
// Same parent-with-sub-tabs pattern as record.js.
//
// Per the Continuum 8-tab reorg (Option B, locked 2026-05-03 with
// Tim).
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { C, FONT as F } from './tokens';
import ContinuumPublications from './publications';
import ContinuumDonorBrief from './donor-brief';

const SUB_TABS = [
  { key: 'publications', label: 'Publications', color: C.purple || C.gold,
    description: 'Claude-generated paper outline / policy brief / talk outline' },
  { key: 'donor',        label: 'Donor Brief',  color: C.amber,
    description: 'Curated funder-ready view: findings summary, watch list, interventions' },
];

export default function ContinuumOutput({ workspaceId, userId }) {
  const storageKey = `continuum_output_subtab_${workspaceId}`;
  const [activeSubTab, setActiveSubTab] = useState(() => {
    if (typeof window === 'undefined') return 'publications';
    return window.localStorage.getItem(storageKey) || 'publications';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, activeSubTab);
  }, [storageKey, activeSubTab]);

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <SubTabNav active={activeSubTab} onSelect={setActiveSubTab} />
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {activeSubTab === 'publications' && (
          <ContinuumPublications workspaceId={workspaceId} userId={userId} />
        )}
        {activeSubTab === 'donor' && (
          <ContinuumDonorBrief workspaceId={workspaceId} userId={userId} />
        )}
      </div>
    </div>
  );
}

function SubTabNav({ active, onSelect }) {
  const activeMeta = SUB_TABS.find(t => t.key === active);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 36,
      borderBottom: `1px solid ${C.b1}`, background: C.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.dim, textTransform: 'uppercase', fontWeight: 600,
      }}>
        ◆ Output
      </div>
      <div style={{ width: 1, height: 14, background: C.b2 }} />
      <div style={{ display: 'flex', gap: 4 }}>
        {SUB_TABS.map(t => {
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onSelect(t.key)}
              title={t.description}
              style={{
                fontSize: 9,
                fontFamily: F.mono,
                letterSpacing: '0.06em',
                padding: '4px 12px',
                borderRadius: 3,
                border: `1px solid ${isActive ? t.color : 'transparent'}`,
                background: isActive ? hexToBg(t.color, 0.10) : 'transparent',
                color: isActive ? t.color : C.static,
                cursor: 'pointer',
                transition: 'all 0.12s',
                textTransform: 'uppercase',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = C.mid;
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = C.static;
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {activeMeta && (
        <div style={{
          marginLeft: 'auto',
          fontFamily: F.sans, fontSize: 10, color: C.dim, fontStyle: 'italic',
        }}>
          {activeMeta.description}
        </div>
      )}
    </div>
  );
}

function hexToBg(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
